import React, { useState, useEffect } from "react";
import api from "../api";
import DataTable from "../components/DataTable";
import CustomButton from "../components/CustomButton";
import ModalForm from "../components/ModalForm";
import CustomTextInput from "../components/CustomTextInput";
import { useNavigate, useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import Spinner from "../components/Spinner";

export default function UsersPage() {
  const navigate = useNavigate();

  const [user, setUser] = useState({});

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [name, setName] = useState("");
  const [lastName, setLastName] = useState("");
  const [govId, setGovId] = useState("");
  const [email, setEmail] = useState("");
  const [contact, setContact] = useState("");
  const [role, setRole] = useState("");
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();

  // Obtener el ordenamiento actual de la URL
  const currentSort = searchParams.get("ordering") || "";
  const currentSearch = searchParams.get("search") || "";

  const handleSortChange = (newSort) => {
    setSearchParams({ ordering: newSort, search: currentSearch });
  };

  const handleSearchChange = (newSearch) => {
    // Actualizar la URL con el nuevo valor de búsqueda
    setSearchParams({ ordering: currentSort, search: newSearch });
  };

  const roles = [
    { value: 1, label: "Coordinador" },
    { value: 2, label: "Administrador" },
  ];

  const columnsConfig = [
    { field: "id", headerName: "ID", sortable: true },
    { field: "govId", headerName: "Cédula de identidad", sortable: true },
    {
      field: "fullName",
      headerName: "Usuario",
      sortable: true,
    },
    {
      field: "role",
      headerName: "Rol de usuario",
      sortable: true,
    },
    { field: "email", headerName: "Correo electrónico", sortable: true },
    { field: "phone", headerName: "Contacto", sortable: true },
  ];

  useEffect(() => {
    async function fetchUsers() {
      try {
        setLoading(true);
        const myResponse = await api.users.getMe();
        const response = await api.users.getMulti(currentSort, "order_by");

        // Añadir campo "name" combinado y "role" con el label correspondiente en los datos
        const usersWithFullNameAndRole = response.map((user) => {
          const role = roles.find((r) => r.value === user.role); // Encuentra el rol correspondiente
          return {
            ...user,
            fullName: `${user.firstName} ${user.lastName}`, // Combina el nombre y el apellido
            role: role ? role.label : "Rol desconocido", // Asigna el label del rol
          };
        });

        setUser(myResponse);
        setData(usersWithFullNameAndRole);
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        setLoading(false);
      }
    }
    fetchUsers();
  }, [currentSort]);

  // Función para manejar el click en "Detalles"
  const handleDetailsClick = (userId) => {
    navigate(`/users/${userId}`); // Redirige a la página de detalles del cliente
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    setFieldErrors({});

    const newUser = {
      firstName: name,
      lastName: lastName,
      govId: govId,
      email: email,
      phone: contact,
      role: role,
    };

    try {
      await api.users.create(newUser);
      setIsModalOpen(false);
      const updatedUsers = await api.users.getMulti();
      const usersWithFullNameAndRole = updatedUsers.map((user) => ({
        ...user,
        fullName: `${user.firstName} ${user.lastName}`,
      }));
      setData(usersWithFullNameAndRole);

      setName("");
      setLastName("");
      setGovId("");
      setEmail("");
      setContact("");
      setRole("");
    } catch (error) {
      if (error.message) {
        // Ajuste para errores específicos de campo según la estructura proporcionada
        const fieldErrors = error.message;
        setFieldErrors(fieldErrors);
      } else {
        setError("Error al crear el usuario. Intenta nuevamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  const [opacityLevel, setOpacityLevel] = useState(0);

  useEffect(() => {
    if (loading) {
      let intervalId = setInterval(() => {
        setOpacityLevel((prev) => Math.min(prev + 0.1, 1)); // Incrementa la opacidad hasta 1
      }, 500); // Ajusta el intervalo para controlar la velocidad
      return () => clearInterval(intervalId); // Limpia el intervalo al desmontar
    } else {
      setOpacityLevel(1); // Asegúrate de que la opacidad esté al máximo al terminar de cargar
    }
  }, [loading]);

  return (
    <Layout>
      <div
        className={`p-8 space-y-10 bg-[#F8F9FB] transition-opacity ease-in-out`}
        style={{
          opacity: opacityLevel,
          transition: "opacity 0.5s linear",
        }}
      >
        {loading ? (
          <Spinner />
        ) : (
          <>
            <h1 className="poppins-bold text-lg text-[#1F384C] tracking-wider">
              Usuarios
            </h1>
            <div className="flex justify-between items-center">
              <p className="poppins-regular text-right text-[#595A5C] text-xs tracking-wider ">
                {data.length} usuarios registrados
              </p>
              <CustomButton
                text="Agregar usuario"
                onClick={() => setIsModalOpen(true)}
                icon={true}
              />
            </div>

            <DataTable
              columnsConfig={columnsConfig}
              data={data}
              loading={loading}
              onActionClick={(row) => handleDetailsClick(row.id)}
              onSortChange={handleSortChange}
              currentSort={currentSort}
              currentSearch={currentSearch}
              onSearchChange={(e) => handleSearchChange(e.target.value)}
            />
            <ModalForm
              title="Agrega un nuevo usuario"
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              onSubmit={handleFormSubmit}
              textBtn="Agregar usuario"
            >
              {/* Muestra un error general si existe */}
              {error && <p className="text-red-600 mb-4">{error}</p>}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <CustomTextInput
                    label="Nombre del usuario"
                    placeholder="Ingresa el nombre del usuario"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    isRequired
                  />
                  {/* Muestra error específico para el campo "firstName" */}
                  {fieldErrors.firstName && (
                    <p className="text-red-600 text-sm">
                      {fieldErrors.firstName[0]}
                    </p>
                  )}
                </div>
                <div>
                  <CustomTextInput
                    label="Apellido del usuario"
                    placeholder="Ingresa el apellido del usuario"
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    isRequired
                  />
                  {/* Muestra error específico para el campo "lastName" */}
                  {fieldErrors.lastName && (
                    <p className="text-red-600 text-sm">
                      {fieldErrors.lastName[0]}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <CustomTextInput
                    label="Cédula de identidad"
                    placeholder="Ingresa tu número de documento"
                    type="text"
                    value={govId}
                    onChange={(e) => setGovId(e.target.value)}
                    isRequired
                  />
                  {/* Muestra error específico para el campo "govId" */}
                  {fieldErrors.govId && (
                    <p className="text-red-600 text-sm">
                      {fieldErrors.govId[0]}
                    </p>
                  )}
                </div>
                <div>
                  <CustomTextInput
                    label="Correo electrónico"
                    placeholder="Ingresa el correo electrónico"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    isRequired
                  />
                  {/* Muestra error específico para el campo "email" */}
                  {fieldErrors.email && (
                    <p className="text-red-600 text-sm">
                      {fieldErrors.email[0]}
                    </p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <CustomTextInput
                    label="Número de contacto"
                    placeholder="Escribe el número de contacto"
                    type="text"
                    value={contact}
                    onChange={(e) => setContact(e.target.value)}
                    isRequired
                  />
                  {/* Muestra error específico para el campo "phone" */}
                  {fieldErrors.phone && (
                    <p className="text-red-600 text-sm">
                      {fieldErrors.phone[0]}
                    </p>
                  )}
                </div>
                <div>
                  <CustomTextInput
                    label="Rol del usuario"
                    placeholder="Selecciona el rol del usuario"
                    type="select"
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    options={roles}
                    isRequired
                  />
                  {/* Muestra error específico para el campo "role" */}
                  {fieldErrors.role && (
                    <p className="text-red-600 text-sm">
                      {fieldErrors.role[0]}
                    </p>
                  )}
                </div>
              </div>
            </ModalForm>
          </>
        )}
      </div>
    </Layout>
  );
}
